import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cssFamily, fontEntry, nearestWeight } from '../../../../model/fonts';
import { layoutText } from '../../../../model/textLayout';
import { withAlpha } from '../../../../model/colors';

// Editing a text in place: a textarea laid exactly over the layer, in the layer's face,
// size, line height, spacing and color, while the canvas leaves that layer out. Escape or
// a click elsewhere commits; an emptied text removes the layer (the editor decides).
export default function TextEditor({ layer, scale, onCommit }) {
  const { t } = useTranslation('artEditor');
  const ref = useRef(null);
  const [text, setText] = useState(layer.text);
  const latest = useRef(text);
  latest.current = text;
  const done = useRef(false);
  const commit = () => {
    if (done.current) return;
    done.current = true;
    onCommit(latest.current);
  };
  useLayoutEffect(() => {
    const element = ref.current;
    element.focus({ preventScroll: true });
    element.select();
  }, []);
  // Unmounting (the selection moved on) commits too: a focused element removed from the
  // page does not reliably blur.
  useEffect(() => () => commit(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const { style } = layer;
  const layout = layoutText(text || ' ', style, layer.width);
  const family = fontEntry(style.font_family).family;
  const shadow = (layer.effects || []).find(effect => effect.visible && effect.type === 'drop_shadow');
  return <textarea ref={ref} className="art-text-editor" value={text} spellCheck aria-label={t('editText')}
    onChange={event => setText(event.target.value)}
    onBlur={commit}
    onPointerDown={event => event.stopPropagation()}
    onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape' || ((event.metaKey || event.ctrlKey) && event.key === 'Enter')) { event.preventDefault(); ref.current.blur(); }
    }}
    style={{
      left: layer.x * scale,
      top: layer.y * scale,
      width: layer.width * scale,
      height: layout.height * scale,
      transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
      padding: `${layout.padY * scale}px ${layout.padX * scale}px`,
      opacity: layer.opacity,
      borderRadius: style.highlight?.mode === 'box' ? style.highlight.radius * scale : 0,
      '--art-font': cssFamily(family),
      '--art-size': `${style.font_size * scale}px`,
      '--art-line': `${layout.lineHeight * scale}px`,
      '--art-weight': nearestWeight(family, style.font_weight),
      '--art-style': style.italic && fontEntry(family).italic ? 'italic' : 'normal',
      '--art-spacing': `${layout.spacing * scale}px`,
      '--art-ink': style.color,
      '--art-fill': style.highlight?.mode === 'box' ? style.highlight.color : 'transparent',
      '--art-align': style.align,
      '--art-case': style.transform === 'uppercase' ? 'uppercase' : 'none',
      '--art-decoration': style.underline ? 'underline' : 'none',
      '--art-shadow': shadow ? `${shadow.x * scale}px ${shadow.y * scale}px ${shadow.blur * scale}px ${withAlpha(shadow.color, shadow.opacity)}` : 'none',
    }} />;
}
